/**
 * Категория меню.
 *
 * Раньше заводилась через окошко браузера с одним полем «название» —
 * а у категории есть цех печати, наценка, родитель и картинка.
 * Всё это приходилось искать по разным экранам или задавать потом,
 * а «потом» обычно не наступает.
 *
 * Родитель выбирается здесь же, поэтому подгруппа заводится из того
 * же окна: раньше для этого была отдельная кнопка у каждой группы,
 * и найти её удавалось не всем.
 */
import { useState } from 'react';

const API = '/api/v1';

/** Цеха печати по-русски: в данных kitchen/bar, человеку — слова. */
const STATION_RU: Record<string, string> = { kitchen: 'Кухня', bar: 'Бар' };

export type CatDraft = {
  id?: string;
  name: string;
  parentId: string | null;
  station: string | null;
  /** наценка точки не добавляется к товарам категории */
  noMarkup: boolean;
  /** исходная наценка, %: правка родителя не должна её стирать */
  markupPct: number | null;
  color: string | null;
  isActive: boolean;
};

export function CategoryDialog({
  token, initial, categories, stations, onClose, onSaved,
}: {
  token: string;
  initial: CatDraft;
  /** для выбора родителя: подгруппа подгруппы не бывает */
  categories: { id: string; name: string; parentId: string | null }[];
  /** цеха печати, заведённые в устройствах */
  stations: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [d, setD] = useState<CatDraft>(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (patch: Partial<CatDraft>) => setD((p) => ({ ...p, ...patch }));

  /*
   * Родителем может быть только группа верхнего уровня.
   *
   * Три уровня вложенности выглядят гибко, а на кассе превращаются
   * в «Бар → Напитки → Холодные → Газировка»: официант делает четыре
   * касания вместо одного и в час пик ошибается.
   */
  const parentName = d.parentId
    ? categories.find((c) => c.id === d.parentId)?.name ?? null
    : null;

  const parents = categories.filter(
    (c) => c.parentId === null && c.id !== d.id,
  );

  const save = async () => {
    if (d.name.trim().length < 2) { setErr('Название слишком короткое'); return; }
    setSaving(true); setErr('');
    try {
      const body = {
        name: d.name.trim(),
        parentId: d.parentId,
        station: d.station,
        // Галочка снята — возвращаем исходную наценку, а не null:
        // иначе смена родителя молча стирала бы заданные проценты
        markupPct: d.noMarkup ? 0 : (d.markupPct === 0 ? null : d.markupPct),
        color: d.color,
        isActive: d.isActive,
      };
      const r = await fetch(
        d.id ? `${API}/menu/categories/${d.id}` : `${API}/menu/categories`,
        {
          method: d.id ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(d.id ? body : { ...body, sort: categories.length }),
        },
      );
      if (!r.ok) {
        // Сервер отвечает кодом причины — покажем её словами,
        // а не пожмём плечами: «не удалось» не лечится
        const data = await r.json().catch(() => null) as { code?: string } | null;
        const RU: Record<string, string> = {
          TOO_DEEP: 'Глубже двух уровней нельзя: выбранный родитель сам подгруппа',
          HAS_CHILDREN: 'У этой группы есть свои подгруппы — сначала перенесите их',
          SELF_PARENT: 'Группа не может лежать внутри самой себя',
          PARENT_NOT_FOUND: 'Родительская группа не найдена — обновите страницу',
          VALIDATION: 'Сервер не принял одно из полей — проверьте название и цвет',
        };
        setErr(RU[data?.code ?? ''] ?? `Не удалось сохранить (${data?.code ?? r.status})`);
        return;
      }
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog" onClick={onClose}>
      <div className="dialog-card cat-dialog" onClick={(e) => e.stopPropagation()}>
        <header className="pd-head">
          <div>
            <span className="eyebrow">Категории</span>
            {/* Заголовок называет, что именно создаём. «Новая категория»
                при выбранном родителе вводит в заблуждение: человек
                ждёт подгруппу, а читает про категорию */}
            <h2>
              {d.id ? 'Категория' : (parentName
                ? `Подгруппа в «${parentName}»`
                : 'Новая категория')}
            </h2>
          </div>
          <button className="pd-x" onClick={onClose} aria-label="Закрыть">×</button>
        </header>

        <div className="pd-body">
          <label>Наименование
            <input autoFocus value={d.name}
              onChange={(e) => set({ name: e.target.value })} />
          </label>

          <label>Родительская категория
            <select value={d.parentId ?? ''}
              onChange={(e) => set({ parentId: e.target.value || null })}>
              <option value="">— верхний уровень —</option>
              {parents.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <i className="split">
              Выберите группу — категория станет её подгруппой.
              Глубже двух уровней не идём: официанту это лишние касания
            </i>
          </label>

          <label>Принтер для цеха
            <select value={d.station ?? ''}
              onChange={(e) => set({ station: e.target.value || null })}>
              <option value="">— не печатать —</option>
              {stations.map((s) => (
                <option key={s} value={s}>{STATION_RU[s] ?? s}</option>
              ))}
            </select>
            <i className="split">
              Куда уйдёт бегунок: холодный цех, горячий, бар. Пусто —
              позиции этой категории на кухню не печатаются
            </i>
          </label>

          <label className="check cat-check">
            <input type="checkbox" checked={d.noMarkup}
              onChange={(e) => set({ noMarkup: e.target.checked })} />
            <span>
              <b>Не учитывать процент наценки</b><br />
              <small>
                Наценка точки продаж не добавляется к товарам этой
                категории. Нужно там, где цена фиксирована: сигареты,
                бизнес-ланч, акционные позиции
              </small>
            </span>
          </label>

          <label>Цвет на кассе
            <div className="cat-colors">
              {[null, '#e8613c', '#e0a33a', '#4f9d69', '#3d7ea6', '#8a5cb8'].map((c) => (
                <button key={c ?? 'none'} type="button"
                  className={`cat-color ${d.color === c ? 'on' : ''}`}
                  style={c ? { background: c } : undefined}
                  onClick={() => set({ color: c })}>
                  {c ? '' : '—'}
                </button>
              ))}
            </div>
            <i className="split">
              Плитки категории на кассе будут этого цвета. Официант
              находит «Напитки» по цвету быстрее, чем читает подписи
            </i>
          </label>

          <label className="check cat-check">
            <input type="checkbox" checked={d.isActive}
              onChange={(e) => set({ isActive: e.target.checked })} />
            <span>
              <b>В работе</b><br />
              <small>
                Выключено — категория и её позиции скрыты с кассы,
                история продаж цела
              </small>
            </span>
          </label>
        </div>

        {err && <p className="rb-warn">{err}</p>}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Отмена</button>
          <button className="btn primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Черновик из строки — или пустой с заданным родителем. */
export function catDraftFrom(
  c?: { id: string; name: string; parentId: string | null; station: string | null;
        markupPct: number | null; color: string | null; isActive: boolean },
  parentId?: string | null,
): CatDraft {
  if (!c) {
    return {
      name: '', parentId: parentId ?? null, station: null,
      noMarkup: false, markupPct: null, color: null, isActive: true,
    };
  }
  return {
    id: c.id,
    name: c.name,
    parentId: c.parentId,
    // Чужие значения из старых данных (импорт, прежние версии) чистим:
    // сервер принимает только kitchen/bar и цвет #rrggbb — иначе любая
    // правка категории падала бы из-за поля, которое человек не трогал
    station: c.station === 'kitchen' || c.station === 'bar' ? c.station : null,
    // Ноль наценки и «не задано» — разные вещи: ноль значит «наценку
    // не добавляем», пусто — «берём у родителя»
    noMarkup: c.markupPct === 0,
    markupPct: c.markupPct,
    color: c.color && /^#[0-9a-fA-F]{6}$/.test(c.color) ? c.color : null,
    isActive: c.isActive,
  };
}
